import { tz } from '@date-fns/tz';
import {
  format,
  startOfDay,
  startOfMonth,
  startOfSecond,
  startOfYear,
} from 'date-fns';

import type {
  ApiInvoiceId,
  ApiOrgId,
  ApiPaymentId,
} from '#/api/types/ApiTypedId';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { StyledVersionedRouterLink } from '#/components/Link/StyledVersionedRouterLink';
import type { I18nFn } from '#/i18n/types/I18nFn';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import { viewInvoiceRoute } from '#/routing/routes';

import { IDEALIST_TIME_ZONE } from './constants/general/idealistTimeZone';
import {
  LINE_ITEM_TYPES,
  LINE_ITEM_TYPES_PLURAL,
} from './constants/general/lineItemTypes';
import { ORG_TYPES } from './constants/general/orgTypes';
import { PAYMENT_TYPES_STAFF } from './constants/general/paymentTypesStaff';
import { STAFF_INVOICE_ACTIVITY_ACTION_TYPES } from './constants/general/staffInvoiceActivityActionTypes';
import { TIERS } from './constants/general/tiers';

export function getStaffInvoiceActivityName(typeId: string) {
  const type = STAFF_INVOICE_ACTIVITY_ACTION_TYPES.find((t) => t.id === typeId);
  return type ? type.name : '';
}

export function getOrgTypeName(i18n: I18nFn, typeId: ApiOrgType) {
  return ORG_TYPES(i18n)[typeId] || '';
}

export function getPaymentMethod(typeId: string) {
  const type = PAYMENT_TYPES_STAFF.find((t) => t.id === typeId);
  return type ? type.name : '';
}

export function getLineItemTypeName(i18n: I18nFn, typeId: string) {
  const type = LINE_ITEM_TYPES(i18n).find((t) => t.id === typeId);
  return type ? type.name : '';
}

export function getLineItemTypePluralName(i18n: I18nFn, typeId: string) {
  const type = LINE_ITEM_TYPES_PLURAL(i18n).find((t) => t.id === typeId);
  return type ? type.name : '';
}

export function getPackTypeName(i18n: I18nFn, typeId: string) {
  return getLineItemTypeName(i18n, typeId);
}

export function getTierName(value?: string) {
  const tier = TIERS.find((t) => t.id === value);
  return tier ? tier.name : '';
}

export function getStaffInvoiceLink(
  orgId: ApiOrgId,
  invoiceId: ApiInvoiceId,
  invoiceNumber: number,
) {
  return (
    <StyledVersionedRouterLink
      to={
        orgId
          ? `/staff/organizations/${orgId}/invoices/${invoiceId}`
          : `/staff/invoices/${invoiceId}`
      }
    >
      #{invoiceNumber}
    </StyledVersionedRouterLink>
  );
}

export function getStaffInvoiceLinkWithPublic(
  orgId: ApiOrgId,
  invoiceId: ApiInvoiceId,
  invoiceNumber: number,
) {
  return (
    <>
      {getStaffInvoiceLink(orgId, invoiceId, invoiceNumber)}
      <br />
      <StyledNativeLinkWithRef href={viewInvoiceRoute.with({ invoiceId })}>
        public link
      </StyledNativeLinkWithRef>
    </>
  );
}

export function getStaffOrgLink(orgId: ApiOrgId, orgName: string) {
  return (
    <StyledVersionedRouterLink to={`/staff/organizations/${orgId}`}>
      {orgName}
    </StyledVersionedRouterLink>
  );
}

export function getStaffPaymentLink(paymentId: ApiPaymentId, amount: string) {
  return (
    <StyledVersionedRouterLink to={`/staff/payments/${paymentId}`}>
      ${amount}
    </StyledVersionedRouterLink>
  );
}

// These functions are meant to generate a standardized timestamp from anywhere in the world
// in the New York time (the idealist timezone). This allows reports to be consistent
// wherever they're generated from.
export function getReportNow() {
  return format(
    startOfSecond(new Date(), {
      in: tz(IDEALIST_TIME_ZONE),
    }),
    "yyyy-MM-dd'T'HH:mm:ss'",
  );
}

export function getReportStartOfDay() {
  return format(
    startOfDay(new Date(), {
      in: tz(IDEALIST_TIME_ZONE),
    }),
    "yyyy-MM-dd'T'HH:mm:ss'",
  );
}

export function getReportStartOfMonth() {
  return format(
    startOfMonth(new Date(), {
      in: tz(IDEALIST_TIME_ZONE),
    }),
    "yyyy-MM-dd'T'HH:mm:ss'",
  );
}

export function getReportStartOfYear() {
  return format(
    startOfYear(new Date(), {
      in: tz(IDEALIST_TIME_ZONE),
    }),
    "yyyy-MM-dd'T'HH:mm:ss'",
  );
}
